import { z } from 'zod';
import { PaymentTermType } from '../types/payment-terms';

// Billing address schema
export const billingAddressSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  contactName: z.string().optional(),
  addressLine1: z.string().min(1, 'Address line 1 is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().optional(),
  postalCode: z.string().min(1, 'Postal code is required'),
  country: z.string().min(1, 'Country is required'),
  vatNumber: z.string().optional(),
});

// Payment terms schema
export const paymentTermsSchema = z
  .object({
    paymentTermType: z.nativeEnum(PaymentTermType),
    daysUntilDue: z.number().int().min(0).max(365).optional(),
  })
  .refine(
    (data) => {
      if (data.paymentTermType === PaymentTermType.CUSTOM) {
        return data.daysUntilDue !== undefined;
      }
      return true;
    },
    {
      message: 'Custom payment terms require daysUntilDue to be specified',
      path: ['daysUntilDue'],
    }
  );

// Line item schema for invoice creation
export const invoiceLineItemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  productId: z.string().optional(),
  featureId: z.string().optional(),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  unitPrice: z.number().min(0, 'Unit price must be non-negative'),
  taxRate: z.number().min(0).max(100, 'Tax rate must be between 0 and 100').optional(),
  discountPercentage: z
    .number()
    .min(0)
    .max(100, 'Discount percentage must be between 0 and 100')
    .optional(),
  periodStart: z.string().datetime().optional(),
  periodEnd: z.string().datetime().optional(),
});

// Create invoice schema
export const createInvoiceSchema = z.object({
  customerId: z.string().optional(),
  dueDate: z.string().datetime().optional(),
  currency: z.string().length(3, 'Currency must be 3 characters').optional().default('GBP'),
  paymentTermsId: z.string().optional(),
  paymentTerms: paymentTermsSchema.optional(),
  purchaseOrderNumber: z.string().optional(),
  purchaseOrderDate: z.string().datetime().optional(),
  periodStart: z.string().datetime().optional(),
  periodEnd: z.string().datetime().optional(),
  billingAddress: billingAddressSchema,
  lineItems: z.array(invoiceLineItemSchema).min(1, 'At least one line item is required'),
  notes: z.string().optional(),
  customerNotes: z.string().optional(),
});

// Update line item schema (for updates, id is required for existing items)
export const updateInvoiceLineItemSchema = z.object({
  id: z.string().optional(), // If provided, update existing item; if not, create new
  description: z.string().min(1).optional(),
  quantity: z.number().min(1).optional(),
  unitPrice: z.number().min(0).optional(),
  taxRate: z.number().min(0).max(100).optional(),
  discountPercentage: z.number().min(0).max(100).optional(),
});

// Update invoice schema
export const updateInvoiceSchema = z.object({
  status: z
    .enum(['draft', 'pending', 'sent', 'paid', 'overdue', 'cancelled', 'refunded'])
    .optional(),
  dueDate: z.string().datetime().optional(),
  paymentMethod: z
    .enum(['credit_card', 'bank_transfer', 'purchase_order', 'cheque', 'cash'])
    .optional(),
  purchaseOrderNumber: z.string().optional(),
  purchaseOrderDate: z.string().datetime().optional(),
  approvedBy: z.string().optional(),
  notes: z.string().optional(),
  customerNotes: z.string().optional(),
  lineItems: z.array(updateInvoiceLineItemSchema).optional(),
});

// Invoice search/filter schema with pagination
export const invoiceSearchSchema = z.object({
  organisationId: z.string().optional(),
  status: z
    .array(z.enum(['draft', 'pending', 'sent', 'paid', 'overdue', 'cancelled', 'refunded']))
    .optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  amountFrom: z.number().min(0).optional(),
  amountTo: z.number().min(0).optional(),
  currency: z.string().length(3).optional(),
  purchaseOrderNumber: z.string().optional(),
  customerName: z.string().optional(),
  // Pagination
  limit: z.number().int().min(1).max(100).default(10),
  offset: z.number().int().min(0).default(0),
});

// Type exports derived from schemas
export type CreateInvoiceSchemaType = z.infer<typeof createInvoiceSchema>;
export type UpdateInvoiceSchemaType = z.infer<typeof updateInvoiceSchema>;
export type InvoiceSearchSchemaType = z.infer<typeof invoiceSearchSchema>;
export type BillingAddressSchemaType = z.infer<typeof billingAddressSchema>;
export type InvoiceLineItemSchemaType = z.infer<typeof invoiceLineItemSchema>;
export type UpdateInvoiceLineItemSchemaType = z.infer<typeof updateInvoiceLineItemSchema>;
export type PaymentTermsSchemaType = z.infer<typeof paymentTermsSchema>;
